import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Edit,
  Eye,
  EyeOff,
  Plus,
  RefreshCw,
  ScrollText,
  Search,
  Trash2,
  Video as VideoIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useUploadQueue } from "@/contexts/UploadQueueContext";
import { cn } from "@/lib/utils";
import { getCategoryLabel, normalizeCategoryList } from "@/lib/category-config";
import VideoUploadDialog from "@/components/admin/VideoUploadDialog";
import VideoEditDialog from "@/components/admin/VideoEditDialog";

interface VideoRow {
  id: string;
  title: string;
  description: string | null;
  video_url: string;
  thumbnail_url: string | null;
  duration: number;
  jlpt_level: "N5" | "N4" | "N3" | "N2" | "N1";
  jlpt_levels?: string[];
  category: string;
  categories?: string[];
  is_premium: boolean;
  is_published: boolean;
  view_count: number;
  like_count: number;
  created_at: string;
  updated_at: string;
  series_id?: string;
}

const levelColor: Record<string, string> = {
  N5: "bg-emerald-500/15 text-emerald-700",
  N4: "bg-sky-500/15 text-sky-700",
  N3: "bg-amber-500/15 text-amber-700",
  N2: "bg-orange-500/15 text-orange-700",
  N1: "bg-rose-500/15 text-rose-700",
};

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${String(secs).padStart(2, "0")}`;
}

function formatBytes(bytes: number): string {
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let index = 0;
  while (value >= 1024 && index < units.length - 1) {
    value /= 1024;
    index += 1;
  }
  return `${value.toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
}

function queueStatusLabel(status: string): string {
  switch (status) {
    case "queued":
      return "待开始";
    case "pending":
      return "排队中";
    case "uploading":
      return "上传中";
    case "processing":
      return "处理中";
    case "completed":
      return "已完成";
    case "error":
      return "失败";
    default:
      return status;
  }
}

function queueStatusBadgeClass(status: string): string {
  switch (status) {
    case "queued":
    case "pending":
      return "bg-secondary text-secondary-foreground border-0";
    case "uploading":
      return "bg-sky-500/15 text-sky-700 border-0";
    case "processing":
      return "bg-amber-500/15 text-amber-700 border-0";
    case "completed":
      return "bg-emerald-500/15 text-emerald-700 border-0";
    case "error":
      return "bg-destructive/15 text-destructive border-0";
    default:
      return "bg-secondary text-secondary-foreground border-0";
  }
}

export default function VideoManagement() {
  const { user } = useAuth();
  const { toast } = useToast();
  const { tasks, startTask, startAll, removeTask, retryTask, clearCompleted, isUploading } =
    useUploadQueue();

  const [videos, setVideos] = useState<VideoRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingVideo, setEditingVideo] = useState<VideoRow | null>(null);
  const [logTaskId, setLogTaskId] = useState<string | null>(null);
  const [logOpen, setLogOpen] = useState(false);

  const fetchVideos = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("videos")
        .select("*")
        .order("updated_at", { ascending: false });

      if (error) throw error;
      setVideos((data || []) as VideoRow[]);
    } catch (error) {
      console.error("Failed to fetch videos", error);
      toast({ title: "获取视频列表失败", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  const openEdit = (video: VideoRow) => {
    setEditingVideo(video);
    setEditOpen(true);
  };

  const handleTogglePublish = async (id: string, currentState: boolean) => {
    try {
      const { error } = await supabase
        .from("videos")
        .update({ is_published: !currentState })
        .eq("id", id);

      if (error) throw error;

      toast({ title: currentState ? "已下架" : "已发布" });
      fetchVideos();
    } catch (error) {
      console.error("Failed to toggle publish", error);
      toast({ title: "操作失败", variant: "destructive" });
    }
  };

  const handleDeleteVideo = async (id: string) => {
    try {
      const { data, error } = await supabase.functions.invoke("delete-video", {
        body: { videoId: id },
      });

      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);

      toast({ title: "视频已删除" });
      fetchVideos();
    } catch (error) {
      console.error("Failed to delete video", error);
      const message = (error as { message?: string })?.message || "删除失败";
      toast({ title: "删除失败", description: message, variant: "destructive" });
    }
  };

  const filteredVideos = useMemo(() => {
    const keyword = searchQuery.trim().toLowerCase();
    if (!keyword) return videos;

    return videos.filter((video) => {
      return (
        video.title.toLowerCase().includes(keyword) ||
        (video.description || "").toLowerCase().includes(keyword)
      );
    });
  }, [videos, searchQuery]);

  const selectedTaskLog = useMemo(
    () => tasks.find((task) => task.id === logTaskId) || null,
    [tasks, logTaskId],
  );

  const selectedTaskLogText = useMemo(() => {
    if (!selectedTaskLog) return "暂无日志";
    const lines = selectedTaskLog.logs?.length
      ? selectedTaskLog.logs
      : selectedTaskLog.error
        ? [selectedTaskLog.error]
        : ["暂无日志"];
    return lines.join("\n");
  }, [selectedTaskLog]);

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">视频管理</h1>
          <p className="text-muted-foreground">上传、编辑和发布视频内容</p>
        </div>
        <Button className="gap-2" onClick={() => setIsAddOpen(true)}>
          <Plus className="h-4 w-4" />
          上传视频
        </Button>
      </div>

      {user ? (
        <VideoUploadDialog
          open={isAddOpen}
          onOpenChange={setIsAddOpen}
          userId={user.id}
          onSuccess={fetchVideos}
        />
      ) : null}

      {editingVideo ? (
        <VideoEditDialog
          open={editOpen}
          onOpenChange={(open) => {
            setEditOpen(open);
            if (!open) setEditingVideo(null);
          }}
          video={editingVideo}
          onSuccess={fetchVideos}
        />
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>上传队列</span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={startAll}
                disabled={tasks.filter((task) => task.status === "queued").length === 0}
              >
                全部开始
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={clearCompleted}
                disabled={tasks.filter((task) => task.status === "completed").length === 0}
              >
                清理完成
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {tasks.length === 0 ? (
            <div className="py-2 text-sm text-muted-foreground">暂无任务</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>标题</TableHead>
                  <TableHead>文件</TableHead>
                  <TableHead>状态</TableHead>
                  <TableHead>进度</TableHead>
                  <TableHead className="text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tasks.map((task) => (
                  <TableRow key={task.id}>
                    <TableCell className="font-medium">
                      <div className="line-clamp-1">{task.metadata.title}</div>
                      {task.error ? <div className="line-clamp-1 text-xs text-destructive">{task.error}</div> : null}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      <div className="line-clamp-1">{task.file.name}</div>
                      <div className="text-xs">{formatBytes(task.file.size)}</div>
                    </TableCell>
                    <TableCell>
                      <Badge className={cn(queueStatusBadgeClass(task.status))}>{queueStatusLabel(task.status)}</Badge>
                      {isUploading && task.status === "uploading" ? (
                        <span className="ml-2 text-xs text-muted-foreground">进行中</span>
                      ) : null}
                    </TableCell>
                    <TableCell className="w-[220px]">
                      <div className="flex items-center gap-2">
                        <Progress value={task.progress} className="h-2" />
                        <span className="w-10 text-right text-xs tabular-nums text-muted-foreground">
                          {task.progress}%
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {task.status === "queued" ? (
                          <Button variant="outline" size="sm" className="h-8" onClick={() => startTask(task.id)}>
                            开始
                          </Button>
                        ) : null}
                        {task.status === "error" ? (
                          <Button variant="outline" size="sm" className="h-8" onClick={() => retryTask(task.id)}>
                            重试
                          </Button>
                        ) : null}
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 gap-1"
                          onClick={() => {
                            setLogTaskId(task.id);
                            setLogOpen(true);
                          }}
                        >
                          <ScrollText className="h-3.5 w-3.5" />
                          日志
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          onClick={() => removeTask(task.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={logOpen} onOpenChange={setLogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              上传日志{selectedTaskLog ? ` - ${selectedTaskLog.metadata.title}` : ""}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[62vh] overflow-y-auto rounded-md border bg-muted/30 p-3">
            <pre className="whitespace-pre-wrap break-words text-xs leading-5">
              {selectedTaskLogText}
            </pre>
          </div>
        </DialogContent>
      </Dialog>

      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="relative max-w-sm flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="搜索视频..."
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                className="pl-10"
              />
            </div>
            <Button variant="outline" size="icon" onClick={fetchVideos}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>视频列表</span>
            <span className="text-sm font-normal text-muted-foreground">共 {filteredVideos.length} 条</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>标题</TableHead>
                  <TableHead>等级</TableHead>
                  <TableHead>分类</TableHead>
                  <TableHead>状态</TableHead>
                  <TableHead>播放/点赞</TableHead>
                  <TableHead className="text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredVideos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                      暂无视频
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredVideos.map((video) => {
                    const levels =
                      Array.isArray(video.jlpt_levels) && video.jlpt_levels.length > 0
                        ? video.jlpt_levels
                        : [video.jlpt_level];

                    const categories = normalizeCategoryList([
                      ...(Array.isArray(video.categories) ? video.categories : []),
                      video.category,
                    ]);

                    return (
                      <TableRow key={video.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-16 items-center justify-center overflow-hidden rounded bg-muted">
                              {video.thumbnail_url ? (
                                <img
                                  src={video.thumbnail_url}
                                  alt={video.title}
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                <VideoIcon className="h-4 w-4 text-muted-foreground" />
                              )}
                            </div>
                            <div>
                              <p className="line-clamp-1 font-medium">{video.title}</p>
                              <p className="text-xs text-muted-foreground">{formatDuration(video.duration)}</p>
                            </div>
                          </div>
                        </TableCell>

                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {levels.map((level) => (
                              <Badge key={level} className={cn("border-0", levelColor[level] || "bg-muted")}>{level}</Badge>
                            ))}
                          </div>
                        </TableCell>

                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {categories.map((category) => (
                              <Badge key={category} variant="outline" className="text-xs">
                                {getCategoryLabel(category)}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>

                        <TableCell>
                          <div className="flex items-center gap-2">
                            {video.is_published ? (
                              <Badge className="bg-emerald-500/15 text-emerald-700 border-0">已发布</Badge>
                            ) : (
                              <Badge variant="secondary">草稿</Badge>
                            )}

                            {video.is_premium ? (
                              <Badge className="bg-amber-500/15 text-amber-700 border-0">付费</Badge>
                            ) : null}
                          </div>
                        </TableCell>

                        <TableCell className="text-sm text-muted-foreground">
                          {video.view_count} / {video.like_count}
                        </TableCell>

                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleTogglePublish(video.id, video.is_published)}
                            >
                              {video.is_published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </Button>

                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(video)}>
                              <Edit className="h-4 w-4" />
                            </Button>

                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive"
                              onClick={() => handleDeleteVideo(video.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
